import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button, Stack, Text } from "@mantine/core";
import { PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { callApiError, doorbellCallsApi } from "@/lib/api/doorbell-calls";

/** The "Ring me" test call (`POST /doorbell/calls/test/`): rings the admin through the banner and web
 *  push like a real call. `ringing` stays true for the call's ring time, so the button can't be
 *  pressed twice. Shared by the guide's Ring me step and the Live calls card on Settings. */
export function useRingMe() {
  const [ringing, setRinging] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const start = useMutation({
    mutationFn: () => doorbellCallsApi.startTest().then((r) => r.data),
    onSuccess: (data) => {
      setRinging(true);
      timer.current = setTimeout(() => setRinging(false), (data.ring_seconds || 30) * 1000);
    },
    onError: (err) => toast.error(callApiError(err, "Couldn't start the test call.")),
  });
  return { ringing, starting: start.isPending, ringMe: () => start.mutate() };
}

/** The Ring me button, with the hint under it while the test call rings. */
export function RingMeButton() {
  const { ringing, starting, ringMe } = useRingMe();
  return (
    <Stack gap="xs" data-testid="ring-me">
      <Button
        leftSection={<PhoneCall size={16} />}
        loading={starting}
        disabled={ringing}
        onClick={ringMe}
        w="fit-content"
      >
        {ringing ? "Ringing…" : "Ring me"}
      </Button>
      {ringing && (
        <Text fz="sm" c="dimmed">
          Answer the call here. Your phone rings too when it has notifications on.
        </Text>
      )}
    </Stack>
  );
}

export default RingMeButton;
